import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { appendTeamMemberLawyerId } from "../../../../styles/styles";

export const getCategories = createAsyncThunk(
  "categories/getCategories",
  async ({ url, token, page, pageSize, teamMemberLawyerId }) => {
    try {
      let paginatedUrl = appendTeamMemberLawyerId(url, teamMemberLawyerId);
      const params = [];
      if (page !== undefined) {
        params.push(`page=${page}`);
      }
      if (pageSize !== undefined) {
        params.push(`page_size=${pageSize}`);
      }
      if (params.length > 0) {
        const separator = paginatedUrl.includes("?") ? "&" : "?";
        paginatedUrl = `${paginatedUrl}${separator}${params.join("&")}`;
      }

      const response = await axios.get(paginatedUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      return {
        ...response.data,
        pagination: {
          page: page || 1,
          pageSize: pageSize || 10,
          rowCount: response.data.count || 0,
          nextPage: response.data.next,
          previousPage: response.data.previous,
        },
      };
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
);

export const updateCategories = createAsyncThunk(
  "categories/updateCategories",
  async ({ url, data, token }) => {
    try {
      const response = await axios.put(url, data, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
);

export const deleteCategories = createAsyncThunk(
  "categories/deleteCategories",
  async ({ url, token }) => {
    try {
      const response = await axios.delete(url, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
);

export const getActivities = createAsyncThunk(
  "getTimeEntry",
  async ({
    url,
    token,
    page,
    pageSize,
    search,
    searchKey = "search",
    teamMemberLawyerId,
  }) => {
    try {
      let paginatedUrl = appendTeamMemberLawyerId(url, teamMemberLawyerId);
      const params = [];
      if (page !== undefined) {
        params.push(`page=${page}`);
      }
      if (pageSize !== undefined) {
        params.push(`page_size=${pageSize}`);
      }
      if (search) {
        params.push(`${searchKey}=${encodeURIComponent(search)}`);
      }
      if (params.length > 0) {
        const separator = paginatedUrl.includes("?") ? "&" : "?";
        paginatedUrl = `${paginatedUrl}${separator}${params.join("&")}`;
      }
      const response = await axios.get(paginatedUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      return {
        ...response.data,
        pagination: {
          page: page || 1,
          pageSize: pageSize || 10,
          rowCount: response.data.count || 0,
          nextPage: response.data.next,
          previousPage: response.data.previous,
        },
      };
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
);

export const updateActivity = createAsyncThunk(
  "categories/updateActivity",
  async ({ url, data, token }) => {
    try {
      const response = await axios.put(url, data, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
);

const ActivitySlice = createSlice({
  name: "activity",
  initialState: {
    allCategories: null,
    allActivities: null,
    loading: false,
    error: null,
    categoriesPagination: {
      page: 1,
      pageSize: 10,
      rowCount: 0,
      nextPage: null,
      previousPage: null,
    },
    activitiesPagination: {
      page: 1,
      pageSize: 10,
      rowCount: 0,
      nextPage: null,
      previousPage: null,
    },
  },
  reducers: {
    setCategoriesPage: (state, action) => {
      state.categoriesPagination.page = action.payload;
    },
    setCategoriesPageSize: (state, action) => {
      state.categoriesPagination.pageSize = action.payload;
    },
    setActivitiesPage: (state, action) => {
      state.activitiesPagination.page = action.payload;
    },
    setActivitiesPageSize: (state, action) => {
      state.activitiesPagination.pageSize = action.payload;
    },
    resetCategoriesPagination: (state) => {
      state.categoriesPagination = {
        page: 1,
        pageSize: 10,
        rowCount: 0,
        nextPage: null,
        previousPage: null,
      };
    },
    resetActivitiesPagination: (state) => {
      state.activitiesPagination = {
        page: 1,
        pageSize: 10,
        rowCount: 0,
        nextPage: null,
        previousPage: null,
      };
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(getCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.allCategories = action?.payload?.results || action?.payload;
        if (action.payload.pagination) {
          state.categoriesPagination = action.payload.pagination;
        }
      })
      .addCase(getCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload;
      })
      .addCase(getActivities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getActivities.fulfilled, (state, action) => {
        state.loading = false;
        state.allActivities = action?.payload?.results || action?.payload;
        if (action.payload.pagination) {
          state.activitiesPagination = action.payload.pagination;
        }
      })
      .addCase(getActivities.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload;
      }),
});

export const {
  setCategoriesPage,
  setCategoriesPageSize,
  setActivitiesPage,
  setActivitiesPageSize,
  resetCategoriesPagination,
  resetActivitiesPagination,
} = ActivitySlice.actions;

export const ActivityReducer = ActivitySlice.reducer;
